import { useQuery } from '@tanstack/react-query';
import React from 'react';
import { Col, Container, Row, Spinner } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { usePageTitle } from '../../../Hooks/usePageTitle';
import { getSubscriptionLogs } from '../../../Services/User/SubscriptionPlans';
import { useGoogleTranslate } from '../../../Hooks/useGoogleTranslate';
import './SubscriptionLogs.css';
import heartDecor from '../../../assets/images/coin-store-figma/decor-heart.svg';

const SubscriptionLogs = () => {
  const pageTitle = useGoogleTranslate('Subscription Logs');
  const loadingLabel = useGoogleTranslate('Loading subscription logs...');
  const noLogs = useGoogleTranslate('No subscription logs available');
  const currentSubscription = useGoogleTranslate('Current Subscription');
  const pastSubscription = useGoogleTranslate('Past Subscription');
  const subscriptionType = useGoogleTranslate('Subscription Type:');
  const subscribedOn = useGoogleTranslate('Subscribed On:');
  const expiresOn = useGoogleTranslate('Expires On:');
  const pricePaid = useGoogleTranslate('Price Paid:');
  const discount = useGoogleTranslate('Discount:');

  usePageTitle(pageTitle);
  const navigate = useNavigate();

  const { data: response, isLoading } = useQuery({
    queryKey: ['subscription-logs'],
    queryFn: getSubscriptionLogs,
  });

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const year = date.getFullYear();
    return `${month}/${day}/${year}`;
  };

  const renderCurrentSubscription = (subscription) => {
    return (
      <div className="subscription-card current-subscription mb-4">
        <h2 className="section-title">{currentSubscription}</h2>
        <h3 className="subscription-card-title border-0">{subscription.subscription_name}</h3>
        <div className="subscription-details">
          <div className="subscription-detail-row border-0">
            <span className="detail-label">{subscriptionType}</span>
            <span className="detail-value">{subscription.subscription_type}</span>
          </div>
          <div className="subscription-detail-row border-0">
            <span className="detail-label">{subscribedOn}</span>
            <span className="detail-value">{formatDate(subscription.subscribed_on)}</span>
          </div>
          <div className="subscription-detail-row border-0">
            <span className="detail-label">{expiresOn}</span>
            <span className="detail-value">{formatDate(subscription.expires_on)}</span>
          </div>
          <div className="subscription-detail-row border-0">
            <span className="detail-label">{pricePaid}</span>
            <span className="detail-value">${subscription.price_paid}</span>
          </div>
          {subscription.discount && (
            <div className="subscription-detail-row border-0">
              <span className="detail-label">{discount}</span>
              <span className="detail-value discount-value">{subscription.discount} 🔥</span>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderPastSubscription = (subscription, index) => {
    return (
      <Col md={6} key={index} className="mb-4 past-subscription-col">
        <div className="past-subscription-item">
          <h4 className="subscription-card-title">{subscription.subscription_name}</h4>
          <div className="subscription-details">
            <div className="subscription-detail-row">
              <span className="detail-label">{subscriptionType}</span>
              <span className="detail-value">{subscription.subscription_type}</span>
            </div>
            <div className="subscription-detail-row">
              <span className="detail-label">{subscribedOn}</span>
              <span className="detail-value">{formatDate(subscription.subscribed_on)}</span>
            </div>
            <div className="subscription-detail-row">
              <span className="detail-label">{pricePaid}</span>
              <span className="detail-value">${subscription.price_paid}</span>
            </div>
            {subscription.discount && (
              <div className="subscription-detail-row">
                <span className="detail-label">{discount}</span>
                <span className="detail-value discount-value">{subscription.discount} 🔥</span>
              </div>
            )}
          </div>
        </div>
      </Col>
    );
  };

  const renderLayout = (content) => (
    <section className="subscription-logs-figma-page">
      <button className="subscription-logs-back-btn" onClick={() => navigate(-1)} aria-label="Go back">
        &#8592;
      </button>
      <span className="subscription-logs-decor subscription-logs-decor-ring-left-top" aria-hidden="true" />
      <span className="subscription-logs-decor subscription-logs-decor-ring-left-bottom" aria-hidden="true" />
      <span className="subscription-logs-decor subscription-logs-decor-ring-right-mid" aria-hidden="true" />
      <span className="subscription-logs-decor subscription-logs-decor-ring-right-top" aria-hidden="true" />
      <img src={heartDecor} alt="" className="subscription-logs-decor subscription-logs-decor-heart" aria-hidden="true" />

      <div className="subscription-logs-figma-hero">
        <Container>
          <h1>{pageTitle}</h1>
        </Container>
      </div>

      <div className="subscription-logs-figma-main">
        <Container className="subscription-logs-figma-container">{content}</Container>
      </div>
    </section>
  );

  if (isLoading) {
    return renderLayout(
      <Row>
        <Col xs={12} className="text-center">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
          <p className="mt-3">{loadingLabel}</p>
        </Col>
      </Row>
    );
  }

  if (!response?.current_subscription && (!response?.past_subscriptions || response.past_subscriptions.length === 0)) {
    return renderLayout(
      <Row>
        <Col xs={12} className="text-center">
          <p className="mt-3">{noLogs}</p>
        </Col>
      </Row>
    );
  }

  return renderLayout(
    <Row>
      <Col xs={12}>
        {response?.current_subscription && (
          <div className="subscription-section mb-5">
            {renderCurrentSubscription(response.current_subscription)}
          </div>
        )}

        {response?.past_subscriptions && response.past_subscriptions.length > 0 && (
          <div className="subscription-section">
            <div className="subscription-card past-subscription-wrapper">
              <h2 className="section-title">{pastSubscription}</h2>
              <Row className="past-subscription-row">
                {response.past_subscriptions.map((subscription, index) =>
                  renderPastSubscription(subscription, index)
                )}
              </Row>
            </div>
          </div>
        )}
      </Col>
    </Row>
  );
};

export default SubscriptionLogs;
